import { useState, useEffect } from "react";
import { Minus, Plus, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export const MIN_STAKE = 0.35;
const CHIPS = [0.35, 1, 2, 5, 10, 25];
const clamp = (v) => Math.max(MIN_STAKE, Math.round((Number(v) || MIN_STAKE) * 100) / 100);

export const StakeSelector = ({ stake, onChange, disabled, payout = 1.89, maxSafe = null, nextStake = null, survive = null, target = 7, safeBase = null, smart = false, onSmartToggle = null, smartBase = null }) => {
  const locked = disabled || smart;
  const [draft, setDraft] = useState(stake.toFixed(2));
  useEffect(() => { setDraft(stake.toFixed(2)); }, [stake]);
  const commit = (v) => { const c = clamp(v); setDraft(c.toFixed(2)); onChange(c); };

  return (
    <div className="rounded-xl border border-gold/40 bg-black/50 p-3 sm:p-4" data-testid="stake-selector">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold/80">Stake</div>
        <div className="text-[11px] font-mono-naevo text-white/60">
          Win pays ≈<span className="text-[color:var(--naevo-success)]" data-testid="stake-payout">${(stake * payout).toFixed(2)}</span> · min ${MIN_STAKE.toFixed(2)}
        </div>
      </div>
      {onSmartToggle && (
        <div className={`mt-2 flex items-center justify-between gap-3 rounded-lg px-3 py-2 border ${smart ? "border-gold/60 bg-gold/10" : "border-white/10 bg-white/5"}`} data-testid="smart-stake-row">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <Sparkles className="h-3.5 w-3.5 text-gold" />
            <span className="text-white/80">Smart Stake</span>
            <span className="text-white/50 font-mono-naevo" data-testid="smart-stake-hint">
              {smart ? `base $${(smartBase ?? stake).toFixed(2)} · covers 7 losses` : "safe base from my balance"}
            </span>
          </div>
          <Switch checked={smart} onCheckedChange={onSmartToggle} disabled={disabled} data-testid="smart-stake-toggle" />
        </div>
      )}
      <div className="mt-2 flex items-center gap-2">
        <button type="button" disabled={locked || stake <= MIN_STAKE} onClick={() => commit(stake - (stake > 1 ? 1 : 0.1))} data-testid="stake-minus" className="h-11 w-11 shrink-0 rounded-full border border-gold/40 text-gold flex items-center justify-center disabled:opacity-40">
          <Minus className="h-4 w-4" />
        </button>
        <div className="flex-1 relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gold font-mono-naevo">$</span>
          <input
            type="number" inputMode="decimal" step="0.01" min={MIN_STAKE}
            value={draft} disabled={locked}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => commit(draft)}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            data-testid="stake-input"
            className="w-full h-11 rounded-full bg-black/60 border border-gold/30 pl-8 pr-4 text-center font-mono-naevo text-lg text-white focus:outline-none focus:border-gold"
          />
        </div>
        <button type="button" disabled={locked} onClick={() => commit(stake + (stake >= 1 ? 1 : 0.1))} data-testid="stake-plus" className="h-11 w-11 shrink-0 rounded-full border border-gold/40 text-gold flex items-center justify-center disabled:opacity-40">
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {nextStake !== null && (
        <div className="mt-2 text-[11px] font-mono-naevo text-white/70" data-testid="stake-next">
          In play: <span className="text-gold">${nextStake.toFixed(2)}</span>{nextStake > stake + 0.001 ? " · recovering, resets to base after a win" : " · base stake"}
        </div>
      )}
      {maxSafe !== null && stake > maxSafe && (
        <div className="mt-2 rounded-lg border border-[#C8102E]/60 bg-[#C8102E]/15 px-3 py-2 text-[11px] text-[#FFB3BE]" data-testid="stake-guard-warning">
          Max Bet Guard: this stake is over 25% of your balance (safe max ${maxSafe.toFixed(2)}).
        </div>
      )}
      {survive !== null && (
        <div className={`mt-2 rounded-lg px-3 py-2 text-[11px] font-mono-naevo flex flex-wrap items-center justify-between gap-2 ${survive >= target ? "border border-[color:var(--naevo-success)]/40 bg-[color:var(--naevo-success)]/10 text-[color:var(--naevo-success)]" : "border border-[#F5A623]/50 bg-[#F5A623]/10 text-[#F5A623]"}`} data-testid="stake-survive">
          <span>Covers <b>{survive}</b> losses in a row · goal {target}</span>
          {!smart && survive < target && safeBase !== null && safeBase >= MIN_STAKE && safeBase < stake && (
            <button type="button" disabled={disabled} onClick={() => commit(safeBase)} className="underline" data-testid="stake-use-safe">Use ${safeBase.toFixed(2)}</button>
          )}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {CHIPS.map((c) => (
          <button key={c} type="button" disabled={locked} onClick={() => commit(c)} data-testid={`stake-chip-${String(c).replace(".", "_")}`}
            className={`h-10 min-w-[52px] px-3 rounded-full text-xs font-mono-naevo font-semibold border-2 border-dashed transition-transform hover:-translate-y-0.5 disabled:opacity-40 ${Math.abs(stake - c) < 0.001 ? "btn-gold border-[#FFF1C1]" : "border-gold/50 text-gold bg-[#C8102E]/20"}`}>
            ${c < 1 ? c.toFixed(2) : c}
          </button>
        ))}
      </div>
    </div>
  );
};
